import React, { useState, useEffect, useCallback } from 'react';
import { getClientContacts, createContact, updateContact, deleteContact } from '../../services/contacts';
import './ContactsTab.css';

export default function ContactsTab({ clientId }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    relationship: 'other',
    is_main_contact: false,
    notes: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClientContacts(clientId);
      setContacts(data);
    } catch (error) {
      console.error('Error loading contacts:', error);
      setError('Failed to load contacts: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingContact) {
        await updateContact(editingContact.id, {
          ...formData,
          client_id: clientId,
        });
        setSuccess('Contact updated successfully!');
      } else {
        await createContact({
          ...formData,
          client_id: clientId,
        });
        setSuccess('Contact added successfully!');
      }

      await loadContacts();
      setShowAddModal(false);
      setEditingContact(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        relationship: 'other',
        is_main_contact: false,
        notes: '',
      });
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      setError(error.message || 'Failed to save contact');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      phone: contact.phone || '',
      email: contact.email || '',
      relationship: contact.relationship,
      is_main_contact: contact.is_main_contact,
      notes: contact.notes || '',
    });
    setShowAddModal(true);
  };

  const handleDelete = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await deleteContact(contactId);
        await loadContacts();
        setSuccess('Contact deleted successfully!');
        setTimeout(() => setSuccess(''), 2000);
      } catch (error) {
        setError('Failed to delete contact: ' + error.message);
      }
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingContact(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      relationship: 'other',
      is_main_contact: false,
      notes: '',
    });
    setError('');
    setSuccess('');
  };

  if (loading) {
    return <div className="loading-state">Loading contacts...</div>;
  }

  return (
    <div className="contacts-tab">
      <div className="section-header">
        <h2>Related Contacts</h2>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          + Add Contact
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {contacts.length === 0 ? (
        <div className="empty-state">
          <p>No contacts added yet. Add your first contact to get started.</p>
        </div>
      ) : (
        <div className="contacts-list">
          {contacts.map((contact) => (
            <div key={contact.id} className={`contact-card ${contact.is_main_contact ? 'main-contact' : ''}`}>
              <div className="contact-header">
                <div className="contact-info">
                  <h3>{contact.name}</h3>
                  {contact.is_main_contact && (
                    <span className="main-contact-badge">Main Contact</span>
                  )}
                </div>
                <div className="contact-actions">
                  <button className="btn-edit" onClick={() => handleEdit(contact)}>
                    Edit
                  </button>
                  <button className="btn-delete" onClick={() => handleDelete(contact.id)}>
                    Delete
                  </button>
                </div>
              </div>
              <div className="contact-details">
                <div className="detail-item">
                  <span className="detail-label">Relationship:</span>
                  <span className="detail-value">{contact.relationship}</span>
                </div>
                {contact.phone && (
                  <div className="detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{contact.phone}</span>
                  </div>
                )}
                {contact.email && (
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{contact.email}</span>
                  </div>
                )}
                {contact.notes && (
                  <div className="detail-item">
                    <span className="detail-label">Notes:</span>
                    <span className="detail-value">{contact.notes}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingContact ? 'Edit Contact' : 'Add New Contact'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="contact-form">
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Contact name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Relationship *</label>
                <select
                  required
                  value={formData.relationship}
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                >
                  <option value="spouse">Spouse</option>
                  <option value="manager">Manager</option>
                  <option value="contractor">Contractor</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_main_contact}
                    onChange={(e) => setFormData({ ...formData, is_main_contact: e.target.checked })}
                  />
                  <span>Main Point of Contact</span>
                </label>
                <small className="form-hint">
                  Only one contact can be marked as the main contact. Selecting this will unset any other main contact.
                </small>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about this contact"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingContact ? 'Update Contact' : 'Add Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
